import React from "react";
import { Box, Rating, Chip, Button, useMediaQuery } from "@mui/material";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { ServiceReview, ReviewerRoleCode } from "../../models/reviewModels";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

// ===================================================================
//  Color config for reviewer roles
// ===================================================================
const roleStyles: Record<ReviewerRoleCode, { bg: string; color: string; border: string }> = {
  [ReviewerRoleCode.MachineOwner]: {
    bg: "#EBF5FB",
    color: "#2980B9",
    border: "#AED6F1",
  },
  [ReviewerRoleCode.ServiceProvider]: {
    bg: "#EAFAF1",
    color: "#27AE60",
    border: "#ABEBC6",
  },
};

// Star gold
const STAR_GOLD = "#F59E0B";

// ===================================================================
//  Props
// ===================================================================
interface RfsReviewSectionProps {
  reviews: ServiceReview[];
  hasReviewed: boolean;
  reviewDismissed: boolean;
  onLeaveReview: () => void;
  onDismissPrompt: () => void;
}

// ===================================================================
//  Main Component
// ===================================================================
const RfsReviewSection: React.FC<RfsReviewSectionProps> = ({
  reviews,
  hasReviewed,
  reviewDismissed,
  onLeaveReview,
  onDismissPrompt,
}) => {
  const { translate } = useTranslation(["servicing", "common"]);
  const isPhone = useMediaQuery("(max-width:768px)");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        animation: "sectionReveal 0.4s ease-out 0.1s both",
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          fontFamily: dt.font,
          textTransform: "uppercase",
          letterSpacing: "1.2px",
          color: dt.textMuted,
        }}
      >
        {translate("servicing:reviews.reviewsSectionTitle")}
      </Box>

      {/* Review prompt banner — only if not yet reviewed and not dismissed */}
      {!hasReviewed && !reviewDismissed && (
        <Box
          sx={{
            display: "flex",
            alignItems: isPhone ? "flex-start" : "center",
            justifyContent: "space-between",
            flexDirection: isPhone ? "column" : "row",
            gap: isPhone ? 1.5 : 2,
            p: isPhone ? 2 : 2.5,
            borderRadius: dt.radius,
            backgroundColor: "#FFFBEB",
            border: "1px solid #FDE68A",
            borderLeft: `4px solid ${STAR_GOLD}`,
            boxShadow: "0 1px 4px rgba(245, 158, 11, 0.08)",
            animation: "sectionReveal 0.35s ease-out both",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                minWidth: 36,
                borderRadius: "50%",
                backgroundColor: "rgba(245, 158, 11, 0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <StarOutlinedIcon sx={{ color: STAR_GOLD, fontSize: 20 }} />
            </Box>
            <Box>
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  fontFamily: dt.font,
                  color: dt.textPrimary,
                  letterSpacing: "-0.1px",
                  mb: 0.25,
                }}
              >
                {translate("servicing:reviews.reviewPromptTitle")}
              </Box>
              <Box
                sx={{
                  fontSize: "13px",
                  fontFamily: dt.font,
                  fontWeight: 400,
                  color: "#92400E",
                  lineHeight: 1.5,
                }}
              >
                {translate("servicing:reviews.reviewPromptDescription")}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexShrink: 0,
              width: isPhone ? "100%" : "auto",
            }}
          >
            {/* Dismiss */}
            <Button
              onClick={onDismissPrompt}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                fontFamily: dt.font,
                color: dt.textSecondary,
                borderRadius: dt.radiusSm,
                px: 2,
                py: 0.75,
                transition: "all 0.2s ease",
                flex: isPhone ? 1 : "none",
                "&:hover": {
                  backgroundColor: "rgba(107, 114, 128, 0.08)",
                  color: dt.textPrimary,
                },
              }}
            >
              {translate("servicing:reviews.notNow") || "Not Now"}
            </Button>

            {/* Leave review CTA */}
            <Button
              onClick={onLeaveReview}
              startIcon={<RateReviewOutlinedIcon sx={{ fontSize: "16px !important" }} />}
              sx={{
                background: `linear-gradient(135deg, ${STAR_GOLD}, #EAB308)`,
                color: "#1a1a2e",
                borderRadius: dt.radiusPill,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "13px",
                fontFamily: dt.font,
                px: 2.5,
                py: 0.75,
                boxShadow: "0 2px 8px rgba(245, 158, 11, 0.25)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                flex: isPhone ? 1 : "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  background: `linear-gradient(135deg, #EAB308, ${STAR_GOLD})`,
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(245, 158, 11, 0.3)",
                },
              }}
            >
              {translate("servicing:reviews.leaveReview")}
            </Button>
          </Box>
        </Box>
      )}

      {/* Review cards */}
      {reviews.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {reviews.map((review, index) => (
            <ReviewEntryCard
              key={review.id}
              review={review}
              index={index}
              isPhone={isPhone}
            />
          ))}
        </Box>
      ) : (
        /* Empty state — only show if already reviewed or dismissed */
        hasReviewed || reviewDismissed ? (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              px: 2,
              borderRadius: dt.radiusSm,
              backgroundColor: dt.cardBg,
              border: `1px solid ${dt.cardBorder}`,
            }}
          >
            <Box sx={{ fontSize: "13px", fontFamily: dt.font, color: dt.textSecondary }}>
              {translate("servicing:reviews.noReviewsYet") || "No reviews yet"}
            </Box>
          </Box>
        ) : null
      )}
    </Box>
  );
};

// ===================================================================
//  Individual Review Card
// ===================================================================
interface ReviewEntryCardProps {
  review: ServiceReview;
  index: number;
  isPhone: boolean;
}

const ReviewEntryCard = ({ review, index, isPhone }: ReviewEntryCardProps) => {
  const { translate } = useTranslation(["servicing"]);
  const roleConfig = roleStyles[review.reviewerRoleCode] || roleStyles[ReviewerRoleCode.MachineOwner];
  const roleLabel =
    review.reviewerRoleCode === ReviewerRoleCode.MachineOwner
      ? translate("servicing:reviews.roleOwner")
      : translate("servicing:reviews.roleProvider");

  return (
    <Box
      sx={{
        borderRadius: dt.radius,
        border: `1px solid ${dt.cardBorder}`,
        borderLeft: `4px solid ${review.reviewerRoleCode === ReviewerRoleCode.MachineOwner ? "#8B5CF6" : STAR_GOLD}`,
        backgroundColor: dt.cardBg,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        animation: `sectionReveal 0.35s ease-out ${0.08 * index}s both`,
        "&:hover": {
          boxShadow: dt.cardShadow,
          transform: "translateY(-1px)",
        },
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          px: isPhone ? 2 : 2.5,
          pt: 2,
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          {/* Reviewer company */}
          <Box
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              fontFamily: dt.font,
              color: dt.textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {review.reviewerCompanyName}
          </Box>
          {/* Role badge */}
          <Chip
            label={roleLabel}
            size="small"
            sx={{
              backgroundColor: roleConfig.bg,
              color: roleConfig.color,
              border: `1px solid ${roleConfig.border}`,
              fontWeight: 700,
              fontFamily: dt.font,
              fontSize: "10px",
              height: "22px",
              letterSpacing: "0.3px",
              textTransform: "uppercase",
            }}
          />
        </Box>

        {/* Date */}
        <Box
          sx={{
            fontSize: "11px",
            fontFamily: dt.fontMono,
            fontWeight: 500,
            color: dt.textMuted,
            letterSpacing: "0.3px",
            flexShrink: 0,
          }}
        >
          {dayjs(review.createdDate).fromNow()}
        </Box>
      </Box>

      {/* Star rating */}
      <Box sx={{ px: isPhone ? 2 : 2.5, pb: 0.5 }}>
        <Rating
          value={review.rating}
          readOnly
          size="small"
          sx={{
            "& .MuiRating-iconFilled": {
              color: STAR_GOLD,
              filter: "drop-shadow(0 1px 2px rgba(245, 158, 11, 0.25))",
            },
            "& .MuiRating-icon": {
              fontSize: "20px",
            },
          }}
        />
      </Box>

      {/* Review title */}
      {review.title && (
        <Box
          sx={{
            px: isPhone ? 2 : 2.5,
            pb: 0.5,
            fontSize: "14px",
            fontWeight: 700,
            fontFamily: dt.font,
            color: dt.textPrimary,
            lineHeight: 1.4,
          }}
        >
          {review.title}
        </Box>
      )}

      {/* Review comment */}
      {review.comment && (
        <Box
          sx={{
            px: isPhone ? 2 : 2.5,
            pb: 2,
            fontSize: "13px",
            fontFamily: dt.font,
            fontWeight: 400,
            color: "#374151",
            lineHeight: 1.65,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {review.comment}
        </Box>
      )}

      {/* If no comment, add bottom padding */}
      {!review.comment && <Box sx={{ pb: 1 }} />}
    </Box>
  );
};

export default RfsReviewSection;
